import { PurchaseStudentType } from "services/pricingModel/model";
import { clearContentCredentials } from "../content";
import { registerPushNotificationToken } from "../firebase";
import { shutdownIntercom } from "../intercom";
import * as logging from "../logging";
import { api } from "../restUtil";
import { User, UserStatus } from "./model";

import { queryClient } from "app/global";
import { routes } from "pages/routes";
import { saveTempNativeLanguage } from "services/i18n";
import {
  currentUserQuery,
  patchUser,
  refreshCurrentUser,
  type UserProfilePatchPayload,
} from "services/users";
import { SessionStorageKeys } from "utils/storage";

type SignUpPayload = {
  email: string;
  passwd: string;
  first_name: string;
  last_name?: string | null;
  city?: string | null;
  country?: string | null;
  timezone?: string | null;
  pricing_model_code?: string;
  student_type?: PurchaseStudentType;
  pricing_model_item_id?: string;
  localization_lang: string;
  native_language?: string | null;
};

export type SignUpParams = SignUpPayload & {
  profile?: UserProfilePatchPayload;
};

type SignUpResponse = {
  user: {
    id: string;
    status: UserStatus;
    email: string | null;
  };
  resend_verification_token: string;
  sso?: {
    redirect_url: string;
  } | null;
};

function createUser(payload: SignUpPayload) {
  return api.post<SignUpPayload, SignUpResponse>("/user/create", payload);
}

export async function signUp({ profile, ...payload }: SignUpParams) {
  const createResp = await createUser(payload);

  if (createResp.user.status === "active") {
    await logIn(payload);

    if (profile) {
      await patchUser(profile);
    }

    await refreshCurrentUser();
  }

  return createResp;
}

type LoginPayload = {
  email: string;
  passwd: string;
};

type LoginResponse = {
  mfa: boolean;
  id: string;
  email: string | null;
  status: UserStatus;
  token: string;
  return_path: string | null;
};

export async function logIn(data: LoginPayload): Promise<LoginResponse> {
  const payload = {
    email: data.email,
    passwd: data.passwd,
  };
  const response = await api.post<LoginPayload, LoginResponse>("/user/login", payload);

  if (!response.mfa) {
    finishSignIn();
  }

  return response;
}

function finishSignIn() {
  void registerPushNotificationToken().catch(ex => {
    logging.logError("Failed to save firebase notification token on sign in", {
      from: "finishSignIn",
      error: ex,
    });
  });
}

type LoginSsoPayload = {
  email: string;
};

type LoginSsoResponse = {
  redirect_url: string;
};

export function signInSSO(email: string) {
  return api.post<LoginSsoPayload, LoginSsoResponse>("/saml/login", { email });
}

type LoginMfaParams = {
  email: string;
  code: string;
  token: string;
};

type LoginMfaPayload = {
  email: string;
  mfa_code: string;
  token: string;
};

type LoginMfaResponse = {
  mfa: false;
  id: string;
  status: UserStatus;
  token: string;
};

export async function signInMfa({
  email,
  code,
  token,
}: LoginMfaParams): Promise<LoginMfaResponse> {
  const payload: LoginMfaPayload = {
    email,
    mfa_code: code,
    token,
  };
  const response = await api.post<LoginMfaPayload, LoginMfaResponse>(
    "/user/login/mfa",
    payload
  );

  finishSignIn();

  return response;
}

type ResendVerifyMessagePayload = {
  resend_verification_token: string;
};

export function resendVerifyMessage(args: ResendVerifyMessagePayload) {
  return api.post<ResendVerifyMessagePayload, void>("/user/verify/resend", args);
}

type ForgotPasswordPayload = {
  email: string;
};

export function forgotPassword(args: ForgotPasswordPayload) {
  return api.post<ForgotPasswordPayload, void>("/user/passwd/uhm", {
    email: args.email,
  });
}

type VerifyUserParams = {
  token: string;
  passwd?: string;
};

type VerifyUserPayload = {
  token: string;
  passwd?: string;
};

type VerifyPasswordResponse = {
  id: string;
  status: UserStatus;
  email: string | null;
};

export function verifyPassword(args: VerifyUserParams) {
  const payload = {
    token: args.token,
    passwd: args.passwd,
  };
  return api.post<VerifyUserPayload, VerifyPasswordResponse>(
    "/user/passwd/verify",
    payload
  );
}

type VerifyUserResponse = VerifyPasswordResponse & {
  jwtoken: string;
};

export function verifyUser(args: VerifyUserParams) {
  const payload = {
    token: args.token as string,
    passwd: args.passwd,
  };
  return api.post<VerifyUserPayload, VerifyUserResponse>("/user/verify", payload);
}

type ChangePasswordPayload = {
  email: string;
  old_passwd: string;
  new_passwd: string;
};

export function changePassword(args: ChangePasswordPayload) {
  return api.post<ChangePasswordPayload, void>("/user/passwd", args);
}

type SamlLogoutResponse = {
  logout_url: string;
};
type LogoutResponse = SamlLogoutResponse | null;
export function logOut() {
  return api.post<undefined, LogoutResponse>("/user/logout");
}

// TODO delete firebase token
export function clearUserSession() {
  void shutdownIntercom().catch(logging.logError);

  logging.clearUserContext();

  sessionStorage.removeItem(SessionStorageKeys.tabId);
  sessionStorage.removeItem(SessionStorageKeys.user);
  saveTempNativeLanguage(undefined);
  clearContentCredentials();

  queryClient.clear();
  queryClient.setQueryData(currentUserQuery().queryKey, null);
}

export function getSessionUser(required?: false): User | null;
export function getSessionUser(required?: true): User;
export function getSessionUser(required?: boolean): User | null;
export function getSessionUser(required: boolean = false) {
  const user = queryClient.getQueryData(currentUserQuery().queryKey);
  if (required && !user) {
    throw new Error("No user in session");
  }
  return user;
}

export function onSessionExpired(serverUserId: string | null) {
  const storedUserId = getSessionUser()?.id ?? null;
  clearUserSession();
  if (serverUserId === null && storedUserId) {
    // FIXME There is no such thing as state in window.location, it's a react-router thing.
    // But we can't use react-router here, so we need to find another way to handle this.
    // window.location.state = {
    //   sessionEpired: true
    // };
    window.location.pathname = routes.public.signIn.path();
  } else if (serverUserId !== storedUserId) {
    window.location.pathname = routes.root.path();
    window.location.reload();
  }
}
